import React, { useEffect, useMemo, useState } from 'react';
import AsyncSelect from 'react-select/async';
import { loadCityLocations } from '../../api/Location';
import { AdminHotel, AdminHotelSaveInput } from '../../types/HotelAdmin';
import { LocationOption } from '../../types/Location';
import ListInput from '../Common/ListInput';
import '../../css/modal.css';

interface HotelFormModalProps {
  isOpen: boolean;
  initialHotel?: AdminHotel | null;
  submitting?: boolean;
  onClose: () => void;
  onSubmit: (payload: AdminHotelSaveInput) => Promise<void> | void;
}

type HotelFormState = {
  name: string;
  city_code: string;
  city_name: string;
  address: string;
  description: string;
  phone_number: string;
  star_rating: string;
  checkin_time: string;
  checkout_time: string;
  amenities: string[];
  retainedImages: string[];
  newImageFiles: File[];
  retainedVideos: string[];
  newVideoFiles: File[];
  latitude: string;
  longitude: string;
  is_active: boolean;
  source_of_truth: boolean;
};

const toText = (value: unknown) => (value === null || value === undefined ? '' : String(value));

const toTime = (value: unknown) => {
  const text = toText(value).trim();
  if (!text) return '';
  return text.length >= 5 ? text.slice(0, 5) : text;
};

const toCityOption = (hotel?: AdminHotel | null): LocationOption | null => {
  if (!hotel?.city_name || !hotel?.city_code) return null;
  return {
    label: String(hotel.city_name),
    value: String(hotel.city_code),
    type: 'city',
  };
};

const toInitialState = (hotel?: AdminHotel | null): HotelFormState => ({
  name: toText(hotel?.name),
  city_code: toText(hotel?.city_code),
  city_name: toText(hotel?.city_name),
  address: toText(hotel?.address),
  description: toText(hotel?.description),
  phone_number: toText(hotel?.phone_number),
  star_rating: toText(hotel?.star_rating),
  checkin_time: toTime(hotel?.checkin_time),
  checkout_time: toTime(hotel?.checkout_time),
  amenities: Array.isArray(hotel?.amenities) ? hotel!.amenities!.map(String).filter(Boolean) : [],
  retainedImages: Array.isArray(hotel?.images) ? hotel!.images!.map(String).filter(Boolean) : [],
  newImageFiles: [],
  retainedVideos: Array.isArray(hotel?.videos) ? hotel!.videos!.map(String).filter(Boolean) : [],
  newVideoFiles: [],
  latitude: toText(hotel?.latitude),
  longitude: toText(hotel?.longitude),
  is_active: typeof hotel?.is_active === 'boolean' ? hotel.is_active : true,
  source_of_truth: typeof hotel?.source_of_truth === 'boolean' ? hotel.source_of_truth : true,
});

const normalizeList = (values: string[]) => {
  return values
    .map((value) => value.trim())
    .filter((value) => value !== '');
};

const HotelFormModal: React.FC<HotelFormModalProps> = ({
  isOpen,
  initialHotel = null,
  submitting = false,
  onClose,
  onSubmit,
}) => {
  const [formData, setFormData] = useState<HotelFormState>(() => toInitialState(initialHotel));
  const [selectedCity, setSelectedCity] = useState<LocationOption | null>(() => toCityOption(initialHotel));
  const [formError, setFormError] = useState('');
  const canCreateObjectUrl =
    typeof URL !== 'undefined' && typeof URL.createObjectURL === 'function';
  const canRevokeObjectUrl =
    typeof URL !== 'undefined' && typeof URL.revokeObjectURL === 'function';

  useEffect(() => {
    if (!isOpen) return;
    setFormData(toInitialState(initialHotel));
    setSelectedCity(toCityOption(initialHotel));
    setFormError('');
  }, [isOpen, initialHotel]);

  const title = useMemo(() => (initialHotel ? 'Edit Hotel' : 'Add Hotel'), [initialHotel]);
  const totalImageCount = formData.retainedImages.length + formData.newImageFiles.length;
  const totalVideoCount = formData.retainedVideos.length + formData.newVideoFiles.length;

  const newImagePreviews = useMemo(
    () =>
      formData.newImageFiles.map((file) => ({
        file,
        url: canCreateObjectUrl ? URL.createObjectURL(file) : '',
      })),
    [formData.newImageFiles, canCreateObjectUrl]
  );

  const newVideoPreviews = useMemo(
    () =>
      formData.newVideoFiles.map((file) => ({
        file,
        url: canCreateObjectUrl ? URL.createObjectURL(file) : '',
      })),
    [formData.newVideoFiles, canCreateObjectUrl]
  );

  useEffect(() => {
    return () => {
      if (!canRevokeObjectUrl) return;
      newImagePreviews.forEach((preview) => {
        if (preview.url) URL.revokeObjectURL(preview.url);
      });
      newVideoPreviews.forEach((preview) => {
        if (preview.url) URL.revokeObjectURL(preview.url);
      });
    };
  }, [newImagePreviews, newVideoPreviews, canRevokeObjectUrl]);

  if (!isOpen) return null;

  const handleInputChange = (
    event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>
  ) => {
    const { name, value, type } = event.target;
    if (type === 'checkbox') {
      const checked = (event.target as HTMLInputElement).checked;
      setFormData((prev) => ({ ...prev, [name]: checked }));
      return;
    }
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const handleImageUpload = (event: React.ChangeEvent<HTMLInputElement>) => {
    const files = Array.from(event.target.files || []).filter((file) =>
      String(file.type || '').startsWith('image/')
    );
    event.target.value = '';
    if (!files.length) return;
    setFormData((prev) => ({
      ...prev,
      newImageFiles: [...prev.newImageFiles, ...files],
    }));
  };

  const handleVideoUpload = (event: React.ChangeEvent<HTMLInputElement>) => {
    const files = Array.from(event.target.files || []).filter((file) =>
      String(file.type || '').startsWith('video/')
    );
    event.target.value = '';
    if (!files.length) return;
    setFormData((prev) => ({
      ...prev,
      newVideoFiles: [...prev.newVideoFiles, ...files],
    }));
  };

  const removeNewImage = (index: number) => {
    setFormData((prev) => ({
      ...prev,
      newImageFiles: prev.newImageFiles.filter((_, idx) => idx !== index),
    }));
  };

  const removeRetainedImage = (index: number) => {
    setFormData((prev) => ({
      ...prev,
      retainedImages: prev.retainedImages.filter((_, idx) => idx !== index),
    }));
  };

  const removeNewVideo = (index: number) => {
    setFormData((prev) => ({
      ...prev,
      newVideoFiles: prev.newVideoFiles.filter((_, idx) => idx !== index),
    }));
  };

  const removeRetainedVideo = (index: number) => {
    setFormData((prev) => ({
      ...prev,
      retainedVideos: prev.retainedVideos.filter((_, idx) => idx !== index),
    }));
  };

  const handleSubmit = async (event: React.FormEvent) => {
    event.preventDefault();
    setFormError('');

    const requiredFields: Array<[string, string]> = [
      ['name', formData.name],
      ['city_code', formData.city_code],
      ['city_name', formData.city_name],
      ['star_rating', formData.star_rating],
      ['checkin_time', formData.checkin_time],
      ['checkout_time', formData.checkout_time],
    ];

    const missingField = requiredFields.find(([, value]) => value.trim() === '');
    if (missingField) {
      setFormError(`Please provide ${missingField[0]}.`);
      return;
    }

    const starRating = Number(formData.star_rating);
    if (!Number.isInteger(starRating) || starRating < 1 || starRating > 5) {
      setFormError('Star rating must be an integer between 1 and 5.');
      return;
    }

    const amenityList = normalizeList(formData.amenities);
    if (amenityList.length < 5) {
      setFormError('Please add at least 5 amenities.');
      return;
    }

    if (!selectedCity || !selectedCity.label || !selectedCity.value) {
      setFormError('Please select a city from the search list.');
      return;
    }

    if (totalImageCount < 3) {
      setFormError('Please add at least 3 images.');
      return;
    }

    const latitude =
      formData.latitude.trim() === '' ? undefined : Number(formData.latitude);
    if (formData.latitude.trim() !== '' && !Number.isFinite(latitude)) {
      setFormError('Latitude must be a valid number.');
      return;
    }

    const longitude =
      formData.longitude.trim() === '' ? undefined : Number(formData.longitude);
    if (formData.longitude.trim() !== '' && !Number.isFinite(longitude)) {
      setFormError('Longitude must be a valid number.');
      return;
    }

    const payload: AdminHotelSaveInput = {
      name: formData.name.trim(),
      city_code: formData.city_code.trim(),
      city_name: formData.city_name.trim(),
      star_rating: starRating,
      checkin_time: formData.checkin_time.trim(),
      checkout_time: formData.checkout_time.trim(),
      is_active: formData.is_active,
      source_of_truth: formData.source_of_truth,
      amenities: amenityList,
      retainedImages: formData.retainedImages,
      newImageFiles: formData.newImageFiles,
      retainedVideos: formData.retainedVideos,
      newVideoFiles: formData.newVideoFiles,
      ...(formData.address.trim() ? { address: formData.address.trim() } : {}),
      ...(formData.description.trim() ? { description: formData.description.trim() } : {}),
      ...(formData.phone_number.trim() ? { phone_number: formData.phone_number.trim() } : {}),
      ...(latitude !== undefined ? { latitude } : {}),
      ...(longitude !== undefined ? { longitude } : {}),
    };

    await onSubmit(payload);
  };

  return (
    <div className="modal-backdrop" onClick={onClose}>
      <div className="modal modal-lg hotels-modal" onClick={(event) => event.stopPropagation()}>
        <div className="modal-header">
          <h2>{title}</h2>
          <button type="button" className="close-btn" onClick={onClose}>
            x
          </button>
        </div>

        <form className="modal-body" onSubmit={handleSubmit}>
          <div className="hotels-form-grid">
            <label>
              Hotel Name *
              <input name="name" value={formData.name} onChange={handleInputChange} />
            </label>
            <label className="hotels-form-city-field">
              City *
              <AsyncSelect<LocationOption, false>
                cacheOptions
                defaultOptions
                loadOptions={loadCityLocations}
                value={selectedCity}
                onChange={(option) => {
                  const city = option as LocationOption | null;
                  setSelectedCity(city);
                  setFormData((prev) => ({
                    ...prev,
                    city_name: city?.label || '',
                    city_code: city?.value || '',
                  }));
                }}
                placeholder="Type city name and select"
                classNamePrefix="hotels-city-rs"
              />
            </label>
            <label>
              Star Rating *
              <input
                type="number"
                min={1}
                max={5}
                step={1}
                name="star_rating"
                value={formData.star_rating}
                onChange={handleInputChange}
              />
            </label>
            <label>
              Phone Number
              <input name="phone_number" value={formData.phone_number} onChange={handleInputChange} />
            </label>
            <label>
              Check-in Time *
              <input
                type="time"
                name="checkin_time"
                value={formData.checkin_time}
                onChange={handleInputChange}
              />
            </label>
            <label>
              Check-out Time *
              <input
                type="time"
                name="checkout_time"
                value={formData.checkout_time}
                onChange={handleInputChange}
              />
            </label>
            <label>
              Latitude
              <input
                name="latitude"
                value={formData.latitude}
                onChange={handleInputChange}
                placeholder="e.g. 25.2048"
              />
            </label>
            <label>
              Longitude
              <input
                name="longitude"
                value={formData.longitude}
                onChange={handleInputChange}
                placeholder="e.g. 55.2708"
              />
            </label>
            <label className="hotels-form-grid-full">
              Address
              <input name="address" value={formData.address} onChange={handleInputChange} />
            </label>
            <label className="hotels-form-grid-full">
              Description
              <textarea
                name="description"
                value={formData.description}
                onChange={handleInputChange}
                rows={3}
              />
            </label>
          </div>

          <div className="hotels-form-grid-full">
            <ListInput
              label="Amenities"
              values={formData.amenities}
              onChange={(amenities) => setFormData((prev) => ({ ...prev, amenities }))}
            />
            <div className="hotels-field-hint">
              Minimum 5 amenities required ({formData.amenities.length}/5)
            </div>
          </div>
          <div className="hotels-form-grid-full">
            <label>
              Images * (minimum 3)
              <input type="file" accept="image/*" multiple onChange={handleImageUpload} />
            </label>
            <div className="hotels-field-hint">
              Total images: {totalImageCount} / 3 required
            </div>
            {formData.retainedImages.length > 0 && (
              <div className="hotels-image-section">
                <div className="hotels-image-section-title">Existing images</div>
                <div className="hotels-image-grid">
                  {formData.retainedImages.map((image, idx) => (
                    <div className="hotels-image-card" key={`existing-${idx}`}>
                      <img src={image} alt={`Existing hotel ${idx + 1}`} />
                      <button
                        type="button"
                        className="file-chip-remove"
                        onClick={() => removeRetainedImage(idx)}
                      >
                        Remove
                      </button>
                    </div>
                  ))}
                </div>
              </div>
            )}
            {newImagePreviews.length > 0 && (
              <div className="hotels-image-section">
                <div className="hotels-image-section-title">New uploads</div>
                <div className="hotels-image-grid">
                  {newImagePreviews.map((preview, idx) => (
                    <div className="hotels-image-card" key={`${preview.file.name}-${idx}`}>
                      {preview.url ? (
                        <img src={preview.url} alt={preview.file.name} />
                      ) : (
                        <div className="hotels-image-card-fallback">{preview.file.name}</div>
                      )}
                      <button
                        type="button"
                        className="file-chip-remove"
                        onClick={() => removeNewImage(idx)}
                      >
                        Remove
                      </button>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>

          <div className="hotels-form-grid-full">
            <label>
              Videos (optional)
              <input type="file" accept="video/*" multiple onChange={handleVideoUpload} />
            </label>
            <div className="hotels-field-hint">
              Total videos: {totalVideoCount}
            </div>
            {formData.retainedVideos.length > 0 && (
              <div className="hotels-image-section">
                <div className="hotels-image-section-title">Existing videos</div>
                <div className="hotels-image-grid">
                  {formData.retainedVideos.map((video, idx) => (
                    <div className="hotels-image-card" key={`existing-video-${idx}`}>
                      <video src={video} controls />
                      <button
                        type="button"
                        className="file-chip-remove"
                        onClick={() => removeRetainedVideo(idx)}
                      >
                        Remove
                      </button>
                    </div>
                  ))}
                </div>
              </div>
            )}
            {newVideoPreviews.length > 0 && (
              <div className="hotels-image-section">
                <div className="hotels-image-section-title">New videos</div>
                <div className="hotels-image-grid">
                  {newVideoPreviews.map((preview, idx) => (
                    <div className="hotels-image-card" key={`${preview.file.name}-${idx}`}>
                      {preview.url ? (
                        <video src={preview.url} controls />
                      ) : (
                        <div className="hotels-image-card-fallback">{preview.file.name}</div>
                      )}
                      <button
                        type="button"
                        className="file-chip-remove"
                        onClick={() => removeNewVideo(idx)}
                      >
                        Remove
                      </button>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>

          <div className="hotels-form-checkboxes">
            <label className="hotels-checkbox">
              <input
                type="checkbox"
                name="is_active"
                checked={formData.is_active}
                onChange={handleInputChange}
              />
              <span>Is Active</span>
            </label>
          </div>

          {formError && <div className="hotels-form-error">{formError}</div>}

          <div className="modal-actions">
            <button type="submit" className="primary" disabled={submitting}>
              {submitting ? 'Saving...' : 'Save'}
            </button>
            <button type="button" className="secondary" onClick={onClose} disabled={submitting}>
              Cancel
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default HotelFormModal;
